import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_eeacbd60-db8b-4da1-8e71-db72803ff0fd/artifacts/f4528czt_Screenshot%202024-07-25%20103325.png";

const SECTION_LABELS = {
  A: { founder: 'Founder Capability', viability: 'Business Viability', validation: 'Market Validation', projections: 'Financial Strategy', security: 'Security' },
  B: { serviceability: 'Serviceability', security: 'Security', trading: 'Trading History', management: 'Management', purpose: 'Funding Purpose' },
  C: { financial: 'Financial Strength', forecasting: 'Forecasting', governance: 'Governance', strategic: 'Strategic Risk' }
};

const SECTION_MAX_SCORES = {
  A: { founder: 30, viability: 20, validation: 20, projections: 15, security: 15 },
  B: { serviceability: 35, security: 20, trading: 20, management: 15, purpose: 10 },
  C: { financial: 30, forecasting: 30, governance: 20, strategic: 20 }
};

export const generatePDF = async (assessment, checklistProgress) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  
  // Colors
  const primaryColor = [15, 23, 42]; // slate-900
  const accentColor = [14, 165, 233]; // sky-500
  const grayColor = [100, 116, 139]; // slate-500
  const lightGray = [241, 245, 249]; // slate-100

  // Helper functions
  const addHeader = (pageNum) => {
    doc.setFontSize(8);
    doc.setTextColor(...grayColor);
    doc.text('EYB Consulting - Client Funding Readiness Report', margin, 10);
    doc.text(`Page ${pageNum}`, pageWidth - margin, 10, { align: 'right' });
  };

  const addFooter = () => {
    doc.setFontSize(7);
    doc.setTextColor(...grayColor);
    doc.text('EYB Consulting provides business advisory services only and does not provide credit advice.', pageWidth / 2, pageHeight - 10, { align: 'center' });
  };

  const getScoreColor = (score) => {
    if (score < 50) return [239, 68, 68]; // red
    if (score < 70) return [245, 158, 11]; // amber
    if (score < 85) return [14, 165, 233]; // sky
    return [16, 185, 129]; // emerald
  };

  // ============ PAGE 1: COVER ============
  // Background
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Logo placeholder (circle)
  doc.setFillColor(255, 255, 255);
  doc.circle(pageWidth / 2, 50, 20, 'F');
  doc.setFillColor(...accentColor);
  doc.circle(pageWidth / 2, 50, 15, 'F');
  
  // Title
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.text('Funding Readiness', pageWidth / 2, 100, { align: 'center' });
  doc.text('Report', pageWidth / 2, 115, { align: 'center' });
  
  // Business Name
  doc.setFontSize(20);
  doc.setFont('helvetica', 'normal');
  doc.text(assessment.business_name, pageWidth / 2, 145, { align: 'center' });
  
  // Score Circle
  const scoreY = 190;
  doc.setFillColor(40, 50, 70);
  doc.circle(pageWidth / 2, scoreY, 35, 'F');
  
  const scoreColor = getScoreColor(assessment.total_score);
  doc.setDrawColor(...scoreColor);
  doc.setLineWidth(4);
  doc.circle(pageWidth / 2, scoreY, 30);
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(36);
  doc.setFont('helvetica', 'bold');
  doc.text(String(assessment.total_score), pageWidth / 2, scoreY + 5, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('out of 100', pageWidth / 2, scoreY + 18, { align: 'center' });
  
  // Category Badge
  doc.setFontSize(14);
  doc.setTextColor(...scoreColor);
  doc.text(assessment.score_category, pageWidth / 2, scoreY + 45, { align: 'center' });
  
  // Date
  doc.setTextColor(180, 180, 180);
  doc.setFontSize(10);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, pageHeight - 30, { align: 'center' });
  
  // ============ PAGE 2: EXECUTIVE SUMMARY ============
  doc.addPage();
  addHeader(2);
  
  let y = 30;
  
  doc.setTextColor(...primaryColor);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Executive Summary', margin, y);
  
  y += 20;
  
  // Summary Box
  doc.setFillColor(...lightGray);
  doc.roundedRect(margin, y, contentWidth, 50, 3, 3, 'F');
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  
  const summaryText = getSummaryText(assessment);
  const splitSummary = doc.splitTextToSize(summaryText, contentWidth - 20);
  doc.text(splitSummary, margin + 10, y + 15);
  
  y += 65;
  
  // Key Metrics Grid
  doc.setTextColor(...primaryColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Assessment Overview', margin, y);
  
  y += 15;
  
  const fundingPurposes = assessment.funding_purposes || [assessment.funding_purpose].filter(Boolean);
  const purposeText = fundingPurposes.length > 0 
    ? fundingPurposes.map(p => (p || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join(', ')
    : 'Not specified';
  
  const metrics = [
    { label: 'Business Type', value: (assessment.business_status || 'N/A').charAt(0).toUpperCase() + (assessment.business_status || '').slice(1) },
    { label: 'Funding Amount', value: formatLoanAmount(assessment.loan_amount_band) || 'Not specified' },
    { label: 'Purpose', value: purposeText || 'Not specified' },
    { label: 'Assessment Path', value: `Path ${assessment.path || 'N/A'}` }
  ];
  
  const colWidth = contentWidth / 2;
  metrics.forEach((metric, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const xPos = margin + (col * colWidth);
    const yPos = y + (row * 25);
    
    doc.setFillColor(...lightGray);
    doc.roundedRect(xPos, yPos, colWidth - 5, 20, 2, 2, 'F');
    
    doc.setFontSize(9);
    doc.setTextColor(...grayColor);
    doc.text(metric.label || '', xPos + 5, yPos + 8);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(String(metric.value || ''), xPos + 5, yPos + 16);
    doc.setFont('helvetica', 'normal');
  });
  
  y += 60;
  
  // Score Interpretation
  doc.setTextColor(...primaryColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('What This Means', margin, y);
  
  y += 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  const interpretation = getScoreInterpretation(assessment.total_score);
  const splitInterp = doc.splitTextToSize(interpretation, contentWidth);
  doc.text(splitInterp, margin, y);
  
  addFooter();
  
  // ============ PAGE 3: SCORE BREAKDOWN ============
  doc.addPage();
  addHeader(3);
  
  y = 30;
  
  doc.setTextColor(...primaryColor);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Score Breakdown', margin, y);
  
  y += 25;
  
  const path = assessment.path || 'B';
  const sectionLabels = SECTION_LABELS[path] || {};
  const maxScores = SECTION_MAX_SCORES[path] || {};
  
  Object.entries(assessment.section_scores || {}).forEach(([key, score]) => {
    const maxScore = maxScores[key] || 100;
    const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
    const label = sectionLabels[key] || key;
    
    // Section name and score
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(String(label), margin, y);
    doc.text(`${score}/${maxScore}`, pageWidth - margin, y, { align: 'right' });
    
    y += 8;
    
    // Progress bar background
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentWidth, 8, 2, 2, 'F');
    
    // Progress bar fill
    const barColor = getScoreColor(percentage);
    doc.setFillColor(...barColor);
    const barWidth = (percentage / 100) * contentWidth;
    doc.roundedRect(margin, y, Math.max(barWidth, 4), 8, 2, 2, 'F');
    
    y += 20;
  });
  
  addFooter();
  
  // ============ PAGE 4: STRENGTHS & IMPROVEMENTS ============
  doc.addPage();
  addHeader(4);
  
  y = 30;
  
  // Strengths
  doc.setTextColor(...primaryColor);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Strengths', margin, y);
  
  y += 15;
  
  assessment.strengths.forEach((strength) => {
    const strengthText = strength.description || '';
    const splitStrength = doc.splitTextToSize(strengthText, contentWidth - 20);
    const boxHeight = Math.max(25, 15 + (splitStrength.length * 5));
    
    doc.setFillColor(236, 253, 245); // emerald-50
    doc.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(6, 95, 70); // emerald-800
    doc.text(strength.section || 'Strength', margin + 10, y + 10);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(splitStrength, margin + 10, y + 18);
    
    y += boxHeight + 5;
  });
  
  y += 10;
  
  // Improvements
  doc.setTextColor(...primaryColor);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Areas for Improvement', margin, y);
  
  y += 15;
  
  assessment.gaps.forEach((gap) => {
    // Calculate content
    const issue = gap.issue || gap.description || '';
    const action = gap.action || '';
    const eybHelp = gap.eyb_help || '';
    
    const splitIssue = doc.splitTextToSize(`Issue: ${issue}`, contentWidth - 20);
    const splitAction = action ? doc.splitTextToSize(`Action: ${action}`, contentWidth - 20) : [];
    const splitEyb = eybHelp ? doc.splitTextToSize(`How EYB Can Help: ${eybHelp}`, contentWidth - 20) : [];
    
    const totalLines = splitIssue.length + splitAction.length + splitEyb.length;
    const boxHeight = Math.max(30, 15 + (totalLines * 5) + (splitEyb.length > 0 ? 10 : 0));
    
    // Check if we need a new page
    if (y + boxHeight > pageHeight - 30) {
      addFooter();
      doc.addPage();
      addHeader(4);
      y = 30;
    }
    
    doc.setFillColor(255, 251, 235); // amber-50
    doc.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, 'F');
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(146, 64, 14); // amber-800
    doc.text(gap.section || 'Improvement Area', margin + 10, y + 10);
    
    let textY = y + 18;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(splitIssue, margin + 10, textY);
    textY += splitIssue.length * 5;
    
    if (splitAction.length > 0) {
      doc.text(splitAction, margin + 10, textY);
      textY += splitAction.length * 5;
    }
    
    if (splitEyb.length > 0) {
      doc.setTextColor(14, 116, 144); // sky-700
      doc.text(splitEyb, margin + 10, textY);
    }
    
    y += boxHeight + 5;
  });
  
  // Add EYB website link
  y += 10;
  doc.setFontSize(10);
  doc.setTextColor(...accentColor);
  doc.text('For more guidance, visit www.eybconsulting.com.au', margin, y);
  
  addFooter();
  
  // ============ PAGE 5: DOCUMENT CHECKLIST ============
  doc.addPage();
  addHeader(5);
  
  y = 30;
  
  doc.setTextColor(...primaryColor);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Document Checklist', margin, y);
  
  y += 10;
  
  const checklist = assessment.checklist || [];
  const completedCount = checklist.filter(item => checklistProgress[item.id]).length;
  const totalCount = checklist.length;
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  doc.text(`${completedCount} of ${totalCount} documents ready`, margin, y);
  
  y += 15;
  
  // Group checklist by group
  const groups = [...new Set(checklist.map(item => item.group))];
  
  groups.forEach((group) => {
    const groupItems = checklist.filter(item => item.group === group);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(String(group || 'Documents'), margin, y);
    
    y += 8;
    
    groupItems.forEach((item) => {
      const isCompleted = checklistProgress[item.id];
      
      // Checkbox
      if (isCompleted) {
        doc.setFillColor(...accentColor);
        doc.roundedRect(margin, y - 4, 5, 5, 1, 1, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.text('✓', margin + 1, y);
      } else {
        doc.setDrawColor(...grayColor);
        doc.setLineWidth(0.5);
        doc.roundedRect(margin, y - 4, 5, 5, 1, 1, 'S');
      }
      
      // Item text
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...(isCompleted ? grayColor : primaryColor));
      doc.text((item.item || '') + (item.required ? ' *' : ''), margin + 10, y);
      
      y += 10;
    });
    
    y += 5;
  });
  
  doc.setFontSize(8);
  doc.setTextColor(...grayColor);
  doc.text('* Required documents', margin, y);
  
  addFooter();
  
  // ============ PAGE 6: DISCLAIMER ============
  doc.addPage();
  addHeader(6);
  
  y = 60;
  
  doc.setTextColor(...primaryColor);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('Important Information', pageWidth / 2, y, { align: 'center' });
  
  y += 30;
  
  doc.setFillColor(...lightGray);
  doc.roundedRect(margin, y, contentWidth, 100, 3, 3, 'F');
  
  const disclaimer = `This Funding Readiness Assessment has been prepared by EYB Consulting for informational purposes only.

EYB Consulting provides business advisory services only and does not provide credit advice. The assessment results, scores, and recommendations contained in this report do not constitute financial advice, credit advice, or a guarantee of funding approval.

Lending decisions are made solely by the lender based on their own assessment criteria, which may differ from the factors considered in this report.

We recommend consulting with a licensed financial advisor or mortgage broker before making any financial decisions.

This report is confidential and intended solely for the use of the individual or entity to whom it is addressed.`;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  const splitDisclaimer = doc.splitTextToSize(disclaimer, contentWidth - 20);
  doc.text(splitDisclaimer, margin + 10, y + 15);
  
  y += 120;
  
  // Contact
  doc.setTextColor(...primaryColor);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Need Help?', pageWidth / 2, y, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayColor);
  doc.text('Contact EYB Consulting for personalized guidance.', pageWidth / 2, y + 12, { align: 'center' });
  
  addFooter();
  
  // Save
  doc.save(`EYB_Client_Assessment_${assessment.business_name.replace(/\s+/g, '_')}.pdf`);
};

function getSummaryText(assessment) {
  const score = assessment.total_score;
  const category = assessment.score_category;
  const business = assessment.business_name;
  
  if (score >= 85) {
    return `${business} demonstrates excellent funding readiness with a score of ${score}/100. This positions the business as a Strong Lending Candidate, likely to receive favorable terms from lenders.`;
  } else if (score >= 70) {
    return `${business} shows good funding readiness with a score of ${score}/100. The business is Funding Ready and should be able to access a range of lending options with some preparation.`;
  } else if (score >= 50) {
    return `${business} has achieved a score of ${score}/100, indicating that some Preparation is Required before approaching lenders. Focus on the improvement areas identified in this report.`;
  } else {
    return `${business} scored ${score}/100, suggesting the business is Not Yet Funding Ready. Significant preparation is recommended before approaching lenders. This report outlines key areas for improvement.`;
  }
}

function getScoreInterpretation(score) {
  if (score >= 85) {
    return "Your business demonstrates strong lending credentials across all key areas. Lenders will likely view your application favorably. Consider negotiating for better terms and rates.";
  } else if (score >= 70) {
    return "Your business is in a good position to approach lenders. You may want to address the identified gaps to potentially improve your terms or access a wider range of products.";
  } else if (score >= 50) {
    return "Your business shows potential but has some areas that need attention. Focus on improving the identified gaps before making formal applications to strengthen your negotiating position.";
  } else {
    return "Your business requires significant preparation before approaching lenders. We recommend working on the improvement areas systematically and reassessing in 3-6 months.";
  }
}

function formatLoanAmount(band) {
  switch (band) {
    case 'under_250k': return 'Under $250,000';
    case '250k_to_1m': return '$250,000 - $1,000,000';
    case 'above_1m': return 'Above $1,000,000';
    default: return band;
  }
}

export default generatePDF;
